import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, Building2, Play, Pause, Code2, Trash2 } from "lucide-react";

// Admin monitor for the Workday employer registry: the hardcoded seed + everything the system
// auto-discovered from real job links. Genuinely-new employers (auto-discovered, not seeded, not
// yet reviewed) are flagged "new" until acknowledged. Light management: review / promote / pause.

function Badge({ children, color }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider border font-bold rounded ${color}`}>
      {children}
    </span>
  );
}

export default function AdminWorkdayTenants() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [company, setCompany] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const urlRef = useRef(null);

  const load = async () => {
    try {
      const [t, sug] = await Promise.all([
        api.get("/admin/apply/workday-tenants"),
        api.get("/admin/apply/workday-suggestions"),
      ]);
      setData(t.data); setSuggestions(sug.data.suggestions || []);
    } catch { toast.error("Could not load employers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (path, body, okMsg) => {
    setBusy(body.tenant);
    try { await api.post(path, body); toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const seedLine = (t) => `        "${t.tenant}/${t.wd}/${t.site}",  # ${t.company}`;
  const copySeed = (t) => {
    navigator.clipboard?.writeText(seedLine(t).trim());
    toast.success(`Copied ${t.company}'s code line (optional — it's already permanent in the database)`);
  };
  const copyAllSeed = () => {
    const lines = (data?.tenants || []).filter((t) => !t.in_seed).map(seedLine).join("\n");
    if (!lines) { toast("No discovered employers to export yet."); return; }
    navigator.clipboard?.writeText(lines);
    toast.success("Copied all discovered employers as code lines (optional)");
  };
  const removeTenant = async (t) => {
    if (!window.confirm(`Remove ${t.company}? We'll stop caching its jobs and drop it from the list. This sticks even across deploys.`)) return;
    await act("/admin/apply/workday-tenants/remove", { tenant: t.tenant }, `Removed ${t.company}`);
  };

  const addByUrl = async () => {
    if (!url.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/admin/apply/workday-tenant", { url: url.trim(), company: company.trim() || null });
      toast.success(`Added ${data.tenant} — ${data.jobs} live jobs`);
      setUrl(""); setCompany(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add — is it a Workday job URL?"); }
    finally { setAdding(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#0025D8]" /></div>;
  const tenants = data?.tenants || [];

  return (
    <div className="min-h-screen bg-[#F7F8FB] px-4 sm:px-8 py-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[#0F172A] mb-4"><ArrowLeft className="w-4 h-4" /> Admin</Link>

        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <h1 className="font-display font-bold text-2xl text-[#0F172A] flex items-center gap-2"><Building2 className="w-6 h-6 text-[#0025D8]" /> Workday employers</h1>
          {data?.new_count > 0 && <Badge color="text-[#0025D8] border-[#0025D8]/40 bg-[#0025D8]/10">{data.new_count} new</Badge>}
          {tenants.some((t) => !t.in_seed) && (
            <button onClick={copyAllSeed} title="Optional — everything here is already saved in the database" className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1 hover:text-[#0025D8] ml-auto">
              <Code2 className="w-3.5 h-3.5" /> Export discovered as code
            </button>
          )}
          <span className={`text-sm text-muted-foreground ${tenants.some((t) => !t.in_seed) ? "" : "ml-auto"}`}>{data?.total || 0} total</span>
        </div>
        <p className="text-sm text-muted-foreground mb-6 max-w-3xl">
          Employers we can find Workday jobs for — built-in defaults plus everything auto-discovered from real job links.
          New ones are flagged until you review them. Everything you do here — add, promote, pause, remove — is saved in the
          database and takes effect immediately. <span className="text-[#0F172A]">No code changes needed.</span>
        </p>

        {/* add by URL */}
        <div className="border border-border rounded-2xl bg-white p-4 mb-6">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> Add an employer by URL</div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input ref={urlRef} value={url} onChange={e => setUrl(e.target.value)} placeholder="Paste any Workday job URL (…myworkdayjobs.com/…/job/…)"
              className="flex-1 rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30" />
            <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company (optional)"
              className="sm:w-52 rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30" />
            <button onClick={addByUrl} disabled={adding}
              className="rounded-lg bg-[#0025D8] text-white text-sm font-semibold px-4 py-2 hover:bg-[#0B2BE8] disabled:opacity-50 inline-flex items-center gap-2 justify-center">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">We read the exact tenant/site off the URL and validate it live — no guessing.</p>
          {suggestions.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/60">
              <div className="text-[11px] font-semibold text-muted-foreground mb-1.5">
                Known Workday employers you don't have yet — click one, then paste their career URL:
              </div>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((n) => (
                  <button key={n} onClick={() => { setCompany(n); urlRef.current?.focus(); }}
                    className="text-xs px-2.5 py-1 rounded-full border border-border bg-muted/40 hover:border-[#0025D8] hover:text-[#0025D8] transition-colors">
                    + {n}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* table */}
        <div className="border border-border rounded-2xl bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">Employer</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Address</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Source</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Jobs cached</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Caching</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.tenant} className={`border-t border-border/60 ${t.is_new ? "bg-[#0025D8]/5" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#0F172A]">{t.company}</span>
                        {t.is_new && <Badge color="text-[#0025D8] border-[#0025D8]/40 bg-[#0025D8]/10">new</Badge>}
                      </div>
                      {t.first_seen && <div className="text-[11px] text-muted-foreground">found {new Date(t.first_seen).toLocaleDateString()}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground whitespace-nowrap">{t.tenant}/{t.wd}/{t.site}</td>
                    <td className="px-4 py-3">
                      <Badge color={t.in_seed ? "text-[#64748B] border-[#64748B]/30" : "text-[#0FA968] border-[#0FA968]/40 bg-[#0FA968]/5"}>
                        {t.source}{t.source === "discovered" ? ` · ${t.match_count}×` : ""}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.cached ? (t.cached_jobs || 0) : "—"}</td>
                    <td className="px-4 py-3">
                      {t.cached
                        ? (t.enabled
                          ? <span className="text-[#059669] text-xs inline-flex items-center gap-1"><Play className="w-3 h-3" /> on</span>
                          : <span className="text-muted-foreground text-xs inline-flex items-center gap-1"><Pause className="w-3 h-3" /> paused</span>)
                        : <span className="text-muted-foreground text-xs">not cached</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {busy === t.tenant && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                        {t.is_new && <button onClick={() => act("/admin/apply/workday-tenants/review", { tenant: t.tenant }, "Marked reviewed")} disabled={busy === t.tenant} className="text-xs font-semibold text-[#0025D8] hover:underline">Mark reviewed</button>}
                        {!t.in_seed && <button onClick={() => copySeed(t)} title="Optional — already permanent. Copies a code line for _ATS_BOARD_SEED (only useful for a brand-new environment)." className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border hover:bg-muted/40 inline-flex items-center gap-1"><Code2 className="w-3 h-3" /> Export</button>}
                        <button onClick={() => removeTenant(t)} title="Remove this employer (sticks across deploys)" disabled={busy === t.tenant} className="grid place-items-center w-7 h-7 rounded-lg text-muted-foreground hover:bg-[#E11D48]/10 hover:text-[#E11D48]"><Trash2 className="w-3.5 h-3.5" /></button>
                        {!t.cached && <button onClick={() => act("/admin/apply/workday-tenants/promote", { tenant: t.tenant }, "Promoted — now caching its jobs")} disabled={busy === t.tenant} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border hover:bg-muted/40">Promote</button>}
                        {t.cached && <button onClick={() => act("/admin/apply/workday-tenants/enabled", { tenant: t.tenant, enabled: !t.enabled }, t.enabled ? "Paused" : "Resumed")} disabled={busy === t.tenant} className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border hover:bg-muted/40">{t.enabled ? "Pause" : "Resume"}</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {tenants.length === 0 && <div className="px-4 py-12 text-center text-sm text-muted-foreground">No employers yet.</div>}
        </div>
      </div>
    </div>
  );
}
